import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import './Pilot.css';

/* The deck is a fixed 1200×700 slide canvas (like PowerPoint). One single
   deck-wide scale (computed in PilotDeck) sizes the whole canvas to the
   viewport, so every slide renders at the exact same size. */
const DESIGN_W = 1280;
const DESIGN_H = 720;

/* Soft, unlisted-page gate. Note: a static React site enforces this in the
   browser only - it keeps the deck off the public site, it is not crypto-grade. */
const PILOT_PASSWORD = 'ccs';
const AUTH_KEY = 'sequester-pilot-auth';

/* ---------- icons ---------- */
const Icon = {
  lock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  ),
  grid: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
  unlock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 7.9-1" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  ),
  left: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  right: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
};

/* ---------- password gate ---------- */
function PilotGate({ onUnlock }) {
  const [val, setVal] = useState('');
  const [error, setError] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (val.trim().toLowerCase() === PILOT_PASSWORD) {
      onUnlock();
    } else {
      setError(true);
      setVal('');
    }
  };

  return (
    <div className="pilot-gate">
      <div className={`pilot-gate-card ${error ? 'is-error' : ''}`}>
        <div className="pilot-gate-lock">{Icon.lock}</div>
        <div className="pilot-gate-wordmark gradient-text">SEQUESTER CCS</div>
        <div className="pilot-gate-sub">Confidential · Investor Briefing</div>
        <form className="pilot-gate-form" onSubmit={submit}>
          <input
            className="pilot-gate-input"
            type="password"
            autoFocus
            autoComplete="off"
            placeholder="Enter access password"
            value={val}
            onChange={(e) => { setVal(e.target.value); if (error) setError(false); }}
            aria-label="Access password"
          />
          <div className="pilot-gate-error">Incorrect password. Please try again.</div>
          <button className="pilot-gate-btn" type="submit">Unlock Presentation</button>
        </form>
        <div className="pilot-gate-foot">Illinois Pilot Project - June 2026</div>
      </div>
    </div>
  );
}

/* ---------- the deck ---------- */
function PilotDeck({ slides, onLock }) {
  const [current, setCurrent] = useState(0);
  const [tocOpen, setTocOpen] = useState(false);
  const total = slides.length;
  const stageRef = useRef(null);
  const touch = useRef({ x: 0, y: 0 });

  const go = useCallback(
    (n) => setCurrent((c) => Math.max(0, Math.min(total - 1, typeof n === 'function' ? n(c) : n))),
    [total]
  );
  const next = useCallback(() => go((c) => c + 1), [go]);
  const prev = useCallback(() => go((c) => c - 1), [go]);

  /* keyboard navigation */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { setTocOpen(false); return; }
      if (e.key === 'ArrowRight' || e.key === 'PageDown') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); prev(); }
      else if (e.key === 'Home') { e.preventDefault(); go(0); }
      else if (e.key === 'End') { e.preventDefault(); go(total - 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, go, total]);

  /* one deck-wide scale, applied to every slide identically */
  const [deckScale, setDeckScale] = useState(1);
  useLayoutEffect(() => {
    const compute = () => {
      // bars overlay the canvas, so use nearly the whole viewport
      const availW = window.innerWidth - 24;
      const availH = window.innerHeight - 24;
      const s = Math.min(availW / DESIGN_W, availH / DESIGN_H, 2.4);
      setDeckScale(s > 0 ? s : 1);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  /* reset the active slide to its top on change (matters in mobile reflow) */
  useEffect(() => {
    const slidesEls = stageRef.current?.querySelectorAll('.pilot-slide');
    const inner = slidesEls && slidesEls[current] && slidesEls[current].querySelector('.pilot-slide-inner');
    if (inner) inner.scrollTop = 0;
  }, [current]);

  /* touch swipe (horizontal) */
  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.4) {
      if (dx < 0) next(); else prev();
    }
  };

  const jump = (i) => { go(i); setTocOpen(false); };

  return (
    <>
      <div className="pilot-progress" style={{ width: `${((current + 1) / total) * 100}%` }} />

      <div className="pilot-topbar">
        <div className="pilot-brand">
          <span className="gradient-text">SEQUESTER CCS</span>
          <span className="tag">Confidential</span>
        </div>
        <div className="pilot-top-actions">
          <button className="pilot-chip" onClick={() => setTocOpen(true)} aria-label="Open contents">
            {Icon.grid}<span>Contents</span>
          </button>
          <button className="pilot-chip" onClick={onLock} aria-label="Lock presentation">
            {Icon.unlock}
          </button>
        </div>
      </div>

      <div
        className="pilot-stage"
        ref={stageRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="pilot-canvas" style={{ '--deck-scale': deckScale }}>
          {slides.map((s, i) => (
            <section
              key={i}
              className={`pilot-slide ${s.slideClass || ''} ${i === current ? 'is-active' : ''}`}
              aria-hidden={i !== current}
            >
              {s.motif && s.motif()}
              <div className="slide-index" aria-hidden="true">{String(i + 1).padStart(2, '0')}</div>
              <div className="pilot-slide-inner">{s.body()}</div>
            </section>
          ))}
        </div>
      </div>

      {current === 0 && (
        <div className="pilot-hint">
          Use <kbd>←</kbd> <kbd>→</kbd> or swipe to navigate
        </div>
      )}

      <div className="pilot-bottombar">
        <div className="pilot-counter">
          <span><b>{String(current + 1).padStart(2, '0')}</b> <span className="sep">/ {total}</span></span>
          <span className="ttl">- {slides[current].title}</span>
        </div>
        <div className="pilot-arrows">
          <button className="pilot-arrow" onClick={prev} disabled={current === 0} aria-label="Previous slide">{Icon.left}</button>
          <button className="pilot-arrow" onClick={next} disabled={current === total - 1} aria-label="Next slide">{Icon.right}</button>
        </div>
      </div>

      <div className={`pilot-toc ${tocOpen ? 'is-open' : ''}`}>
        <div className="pilot-toc-head">
          <h3>Contents</h3>
          <button className="pilot-chip" onClick={() => setTocOpen(false)}>{Icon.close}<span>Close</span></button>
        </div>
        <div className="pilot-toc-grid">
          {slides.map((s, i) => (
            <button
              key={i}
              className={`pilot-toc-item ${i === current ? 'is-current' : ''}`}
              onClick={() => jump(i)}
            >
              <span className="pilot-toc-num">{String(i + 1).padStart(2, '0')}</span>
              <span className="pilot-toc-title">{s.title}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

/* ---------- page root ---------- */
export default function Pilot({ slides, docTitle = 'Sequester CCS - Pilot Project' }) {
  const [authed, setAuthed] = useState(() => {
    try { return sessionStorage.getItem(AUTH_KEY) === 'true'; } catch { return false; }
  });

  useEffect(() => {
    document.body.classList.add('pilot-mode');
    const prevTitle = document.title;
    document.title = docTitle;
    return () => {
      document.body.classList.remove('pilot-mode');
      document.title = prevTitle;
    };
  }, [docTitle]);

  const unlock = () => {
    try { sessionStorage.setItem(AUTH_KEY, 'true'); } catch (e) { /* ignore */ }
    setAuthed(true);
  };
  const lock = () => {
    try { sessionStorage.removeItem(AUTH_KEY); } catch (e) { /* ignore */ }
    setAuthed(false);
  };

  return (
    <div className="pilot">
      {authed ? <PilotDeck slides={slides} onLock={lock} /> : <PilotGate onUnlock={unlock} />}
    </div>
  );
}
